import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react"
import localFont from "next/font/local"
import { type FC, Fragment, type ReactNode, useEffect, useRef, useState } from "react"

// this is added as duplication, as unclear why otherwise font variables are not defined
const roboto_mono = localFont({
  display: "swap",
  preload: true,
  src: "../public/assets/fonts/roboto_mono/RobotoMono.ttf",
  style: "normal",
  variable: "--font-roboto",
  weight: "100 700",
})

// Font files can be located inside of `pages`
const blur = localFont({
  preload: true,
  src: "../public/assets/fonts/blur/Blur-Regular.ttf",
  variable: "--font-blur",
})

interface VisualViewportBounds {
  height: number
  left: number
  top: number
  width: number
}

export const ModalBase: FC<{
  logo?: {
    src: string
    styles?: string
  }
  show: boolean
  title?: string
  subTitle?: string
  titleStyles?: string
  onClose?: () => void
  dialogStyles?: string
  addBgOpacity?: boolean
  subTitleStyles?: string
  showCloseIcon?: boolean
  dialogViewportStyles?: string
  dialogContainerStyles?: string
  dialogPanelStyles?: string
  children?: ReactNode | ReactNode[]
  footer?: ReactNode
  allowPanelOverflowVisible?: boolean
  disableInitialFocus?: boolean
  disableVisualViewportTracking?: boolean
}> = ({
  logo,
  show,
  title,
  onClose,
  children,
  subTitle,
  footer,
  titleStyles = "",
  dialogStyles = "",
  addBgOpacity = false,
  showCloseIcon = false,
  subTitleStyles = "",
  dialogViewportStyles = "",
  dialogContainerStyles = "",
  dialogPanelStyles = "py-16 px-[84px]",
  allowPanelOverflowVisible = false,
  disableInitialFocus = false,
  disableVisualViewportTracking = false,
}) => {
  const initialFocusRef = useRef(null)
  const [viewportBounds, setViewportBounds] = useState<VisualViewportBounds | null>(null)

  useEffect(() => {
    if (!show || typeof window === "undefined" || disableVisualViewportTracking) {
      setViewportBounds(null)
      return
    }

    const visualViewport = window.visualViewport
    if (!visualViewport) {
      setViewportBounds(null)
      return
    }

    const updateViewportBounds = () => {
      setViewportBounds({
        height: visualViewport.height,
        left: visualViewport.offsetLeft,
        top: visualViewport.offsetTop,
        width: visualViewport.width,
      })
    }

    updateViewportBounds()
    visualViewport.addEventListener("resize", updateViewportBounds)
    visualViewport.addEventListener("scroll", updateViewportBounds)
    window.addEventListener("orientationchange", updateViewportBounds)

    return () => {
      visualViewport.removeEventListener("resize", updateViewportBounds)
      visualViewport.removeEventListener("scroll", updateViewportBounds)
      window.removeEventListener("orientationchange", updateViewportBounds)
    }
  }, [show, disableVisualViewportTracking])

  const viewportStyle = viewportBounds
    ? {
        height: `${viewportBounds.height}px`,
        left: `${viewportBounds.left}px`,
        top: `${viewportBounds.top}px`,
        width: `${viewportBounds.width}px`,
      }
    : undefined

  return (
    <Transition show={show}>
      <Dialog
        as="div"
        className={`relative z-30 ${roboto_mono.variable} ${blur.variable} ${dialogStyles}`}
        initialFocus={disableInitialFocus ? undefined : initialFocusRef}
        onClose={
          onClose
            ? onClose
            : () => {
                //Close modal logic
              }
        }
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={`fixed inset-0 ${addBgOpacity ? "bg-[#232734] bg-opacity-60" : ""}`} />
        </TransitionChild>

        <div
          className={`${viewportBounds ? "fixed" : "fixed inset-0"} ${
            dialogViewportStyles || "overflow-y-auto overscroll-contain"
          }`}
          style={viewportStyle}
        >
          <div
            className={`flex min-h-full items-center justify-center px-6 sm:p-4 text-center ${dialogContainerStyles}`}
          >
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="flex flex-col items-center">
                <DialogPanel
                  className={`transform ${
                    allowPanelOverflowVisible ? "overflow-visible" : "overflow-hidden"
                  } bg-surfacePrimary text-left align-middle transition-all ${dialogPanelStyles}`}
                >
                  {onClose && (
                    <button
                      className="absolute top-0 right-0 z-10 cursor-pointer p-5"
                      onClick={onClose}
                      type="button"
                    >
                      <img
                        alt=""
                        className={`w-8 h-8 object-contain ${showCloseIcon ? "" : "sm:hidden"}`}
                        src="/assets/images/icons/close-delete.svg"
                      />
                    </button>
                  )}
                  {!!logo?.src && <img alt="" className={logo.styles} src={logo.src} />}
                  {!!title && (
                    <DialogTitle
                      as="h3"
                      className={`text-[40px] sm:text-[60px] font-bold leading-[1] uppercase text-center ${titleStyles}`}
                    >
                      {title}
                    </DialogTitle>
                  )}
                  {!!subTitle && (
                    <DialogTitle
                      as="h3"
                      className={`text-[20px] sm:text-[30px] text-center ${subTitleStyles}`}
                    >
                      {subTitle}
                    </DialogTitle>
                  )}
                  <div ref={initialFocusRef}>{children}</div>
                </DialogPanel>
                {footer}
              </div>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
